import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ShieldAlert } from "lucide-react";
import { setupAccount } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const REGIONS = [
  { code: "IN-MH", name: "Maharashtra" },
  { code: "IN-DL", name: "Delhi" },
  { code: "IN-KA", name: "Karnataka" },
  { code: "IN-TN", name: "Tamil Nadu" },
  { code: "IN-UP", name: "Uttar Pradesh" },
  { code: "IN-WB", name: "West Bengal" },
  { code: "IN-GJ", name: "Gujarat" },
  { code: "IN-RJ", name: "Rajasthan" },
  { code: "IN-PB", name: "Punjab" },
  { code: "IN-KL", name: "Kerala" },
  { code: "IN-TG", name: "Telangana" },
  { code: "IN-HR", name: "Haryana" },
  { code: "GLOBAL", name: "Global (NRI / Outside India)" },
];

export default function Welcome() {
  const { user, setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [region, setRegion] = useState(user?.region || "IN-MH");
  const [age18, setAge18] = useState(false);
  const [terms, setTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!user) {
    // First-render flicker before AuthContext has hydrated. Show a brief
    // loading state instead of bouncing to "/" — that bounce was the source
    // of the "sign-in lands back on landing page" bug.
    return (
      <div className="min-h-screen flex items-center justify-center text-white/50 text-sm">
        Loading your season…
      </div>
    );
  }

  const submit = async () => {
    if (!age18 || !terms) return;
    setLoading(true);
    try {
      const u = await setupAccount({ region, age_verified: true });
      setUser(u);
      await refresh();
      // Clear tour-seen so the QuickTour auto-pops on Dashboard for this new user
      try { localStorage.removeItem("fanpuff_tour_seen"); } catch (e) { /* noop */ }
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-5" data-testid="welcome-page">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md rounded-3xl glass-strong p-7"
      >
        <div className="chip inline-flex" style={{ background: "#B026FF22", color: "#B026FF", border: "1px solid #B026FF55" }}>
          Welcome
        </div>
        <h1 className="mt-3 font-display text-3xl font-bold">Hi {user.name?.split(" ")[0] || "Fan"},</h1>
        <p className="text-sm text-white/65 mt-1">Let's set up your season. Just a couple of quick checks.</p>

        <div className="mt-6">
          <label className="text-xs tracking-widest uppercase font-bold text-white/50">Region / State</label>
          <select
            data-testid="region-select"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="mt-2 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-3 text-white outline-none focus:ring-2 focus:ring-[#B026FF]/60"
          >
            {REGIONS.map((r) => (
              <option key={r.code} value={r.code} className="bg-[#0A0A10]">{r.name}</option>
            ))}
          </select>
        </div>

        <div className="mt-5 space-y-3">
          <Check18 checked={age18} onChange={setAge18} tid="age-check" label="I confirm I am 18 years or older." />
          <Check18 checked={terms} onChange={setTerms} tid="terms-check" label="I understand Fanpuff is free-to-play, virtual-only, and not a real-money game." />
        </div>

        <button
          data-testid="welcome-submit"
          disabled={!age18 || !terms || loading}
          onClick={submit}
          className="mt-6 w-full py-3 rounded-full font-bold text-black tracking-wide disabled:opacity-40"
          style={{ background: "linear-gradient(90deg, #00F0FF, #B026FF)" }}
        >
          {loading ? "Starting…" : "Enter Fanpuff"}
        </button>

        <div className="mt-5 flex items-start gap-2 text-[11px] text-white/45">
          <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <p>Rewards on Fanpuff are non-cash (badges, merch, experiences). No deposits, no withdrawals.</p>
        </div>
      </motion.div>
    </div>
  );
}

function Check18({ checked, onChange, label, tid }) {
  return (
    <button
      type="button"
      data-testid={tid}
      onClick={() => onChange(!checked)}
      className={`w-full text-left flex items-start gap-3 rounded-2xl border p-3 transition ${checked ? "border-[#B026FF] bg-[#B026FF]/10" : "border-white/10 bg-white/5"}`}
    >
      <span className={`w-5 h-5 rounded-md grid place-items-center shrink-0 ${checked ? "bg-[#00F0FF] text-black" : "bg-white/10"}`}>
        {checked && <Check className="w-4 h-4" />}
      </span>
      <span className="text-sm text-white/85">{label}</span>
    </button>
  );
}
